/** Saved routes — house sheet pattern (see StatePicker/LayersPanel): App.tsx
 * owns the planner reducer and the map; this only reads plan/store.ts (a
 * synchronous localStorage read, cheap enough to own locally rather than
 * threading a `plans` list through App.tsx state) and calls back for
 * anything that touches the reducer or the map (new route, resume, open). */
import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { formatGainFt, formatMiles } from "../plan/geo";
import { gpxFilename, planToGPX } from "../plan/gpx";
import { parseGPX } from "../plan/gpximport";
import { encodeShareURL } from "../plan/share";
import { deletePlan, listPlans, loadDraft } from "../plan/store";
import type { ElevationSample, Plan, SavedPlan } from "../plan/types";

function savedDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Triggers a browser download of `xml` as `filename` via a throwaway
 * a[download] — the standard client-side "save this Blob as a file"
 * pattern, no server round-trip. */
function downloadGPX(filename: string, xml: string) {
  const url = URL.createObjectURL(new Blob([xml], { type: "application/gpx+xml" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function PlansSheet({
  onClose,
  onNewRoute,
  onResumeDraft,
  onOpenPlan,
  onImportPlan,
  onShare,
  region,
  openPlanId,
  openPlanSamples,
}: {
  onClose: () => void;
  onNewRoute: () => void;
  onResumeDraft: (draft: Plan) => void;
  onOpenPlan: (saved: SavedPlan) => void;
  /** A GPX file the user picked via IMPORT GPX, already parsed to a Plan —
   * App.tsx enters draw mode with it the same way it does a resumed draft. */
  onImportPlan: (plan: Plan) => void;
  /** A saved plan's SHARE button, once its URL has finished encoding —
   * App.tsx owns the ShareSheet overlay itself (see its own one-overlay-
   * at-a-time rule), this just builds the payload and hands it up. */
  onShare: (share: { plan: SavedPlan; url: string }) => void;
  /** The region an import lands in — there's no per-file region metadata
   * to read instead, so it's whatever state is active when the sheet
   * opens (App.tsx's own `state.region`). */
  region: string;
  /** The plan currently open in the drawer (if any) and its cached terrain
   * samples — lets a GPX download reuse them instead of resampling; per
   * spec a download must never itself block on sampling. */
  openPlanId: string | null;
  openPlanSamples: ElevationSample[] | null;
}) {
  const [plans, setPlans] = useState<SavedPlan[]>(() => listPlans());
  const [draft] = useState<Plan | null>(() => loadDraft());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Which plan's GPX button is showing its post-download "SAVED" swap —
  // one id at a time is enough, downloads aren't concurrent.
  const [savedId, setSavedId] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  // The in-repo pattern for "focus the thing you'd reach for first" —
  // SearchPalette does the same for its query input on mount.
  const newRouteRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    newRouteRef.current?.focus();
  }, []);

  const handleImportChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // clears the picked filename so re-importing the same file fires onChange again
    if (!file) return;
    setImportError(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      const plan = parseGPX(text, file.name, region, new Date().toISOString());
      if (!plan) {
        setImportError("Couldn't read that GPX.");
        return;
      }
      onImportPlan(plan);
    };
    reader.onerror = () => setImportError("Couldn't read that GPX.");
    reader.readAsText(file);
  };

  const handleDownload = (saved: SavedPlan) => {
    // An empty samples array means "sampling hasn't resolved (or failed)",
    // not "an elevation profile with zero points" — planToGPX would treat
    // [] as the with-samples branch and emit an empty track, so only a
    // non-empty cache rides along.
    const samples =
      saved.id === openPlanId && openPlanSamples && openPlanSamples.length > 0
        ? openPlanSamples
        : undefined;
    downloadGPX(gpxFilename(saved), planToGPX(saved, samples));
    // Zero-typing save deserves the same "did that work?" acknowledgment
    // as DONE's toast — swap the label back after ~1s (guarded so a second
    // download started in the meantime doesn't get its "SAVED" clobbered
    // early by this timer).
    setSavedId(saved.id);
    setTimeout(() => setSavedId((id) => (id === saved.id ? null : id)), 1000);
  };

  const handleShare = (saved: SavedPlan) => {
    void encodeShareURL(saved).then((url) => onShare({ plan: saved, url }));
  };

  const handleDelete = (id: string) => {
    deletePlan(id);
    setPlans(listPlans());
    setConfirmDeleteId(null);
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="routes-heading"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // stopPropagation matters the same way it does for ShareSheet's
            // own Escape handler: PLAN is disabled mid-draw today so there's
            // no live App-level listener to fight, but the discipline is
            // cheap and keeps this dialog self-contained if that changes.
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <h2 id="routes-heading">Routes</h2>
        <button
          ref={newRouteRef}
          className="plans-primary-btn"
          onClick={onNewRoute}
          title="Start drawing a new route"
        >
          NEW ROUTE
        </button>
        <button
          className="plans-secondary-btn"
          onClick={() => {
            setImportError(null);
            importInputRef.current?.click();
          }}
          title="Open a GPX file as a new route"
        >
          IMPORT GPX
        </button>
        {/* Hidden native proxy for the button above, which is the one real
          * control a keyboard/AT user interacts with — tabIndex=-1 +
          * aria-hidden keep this out of both the tab order and the
          * accessibility tree so it doesn't show up as a second, unlabeled
          * "choose file" stop right after IMPORT GPX. */}
        <input
          ref={importInputRef}
          type="file"
          accept=".gpx"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={handleImportChange}
        />
        {importError && <p className="plans-import-error">{importError}</p>}
        <ul className="picker-list">
          {draft && draft.points.length >= 2 && (
            <li>
              <button className="row" onClick={() => onResumeDraft(draft)}>
                <span>Draft — resume</span>
                <span className="row-caption">{draft.points.length} points</span>
              </button>
            </li>
          )}
          {plans.length === 0 && <li className="plans-empty">No saved routes yet.</li>}
          {[...plans]
            .sort((a, b) => b.savedAt.localeCompare(a.savedAt))
            .map((saved) => (
              <li key={saved.id}>
                <div className="row plan-row">
                  <button className="plan-row-open" onClick={() => onOpenPlan(saved)}>
                    <span>{saved.name || "Untitled route"}</span>
                    <span className="row-caption">
                      {formatMiles(saved.stats.distanceM)}
                      {saved.stats.gainM != null ? ` · ${formatGainFt(saved.stats.gainM)}` : ""}
                      {` · saved ${savedDate(saved.savedAt)}`}
                    </span>
                  </button>
                  <div className="plan-row-actions">
                    {confirmDeleteId === saved.id ? (
                      <>
                        <span className="plan-row-confirm-label">Delete?</span>
                        <button
                          className="plan-row-action-btn plan-row-action-danger"
                          onClick={() => handleDelete(saved.id)}
                        >
                          DELETE
                        </button>
                        <button className="plan-row-action-btn" onClick={() => setConfirmDeleteId(null)}>
                          KEEP
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="plan-row-action-btn" onClick={() => handleDownload(saved)}>
                          {savedId === saved.id ? "SAVED" : "GPX"}
                        </button>
                        <button className="plan-row-action-btn" onClick={() => handleShare(saved)}>
                          SHARE
                        </button>
                        <button
                          className="plan-row-action-btn"
                          onClick={() => setConfirmDeleteId(saved.id)}
                        >
                          DELETE
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}
